/**
 * The pot being built, and the arithmetic every screen downstream of it reads.
 *
 * Held above both steps because it is the same pot in both: what went in
 * (`PotBuilder`) and how it is divided (`PotDivider`) are two questions about
 * one thing, and going back to change an ingredient must move every per-portion
 * figure on the divider — and, after it is stored, the pot detail and the page a
 * link opens. One source, derived downwards.
 *
 * ## Raw and cooked, and why both are here
 *
 * The ingredients are raw weights: 500 g of mince, 480 g of beans. What comes
 * out of the pan weighs less, because water leaves. Calories do not, so the
 * whole difference is a density:
 *
 *     raw 1,590 g → cooked 1,380 g, at the same 2,870 kcal
 *     a quarter is 345 g at 208 kcal / 100 g
 *
 * Without a cooked weight none of that is knowable and a portion is a
 * *fraction* — right on calories, with no gram figure to log against. That is
 * the state this starts in, and it is a legitimate place to stay: weighing is
 * offered and stated as optional.
 *
 * ## Scaling is not editing the recipe
 *
 * `scale` multiplies the ingredient weights for this pot only. Sunday's double
 * batch and Wednesday's single one are the same saved meal cooked twice, and
 * the recipe is the starting point rather than the record — which is also why
 * every edit here is local, and why the offer to save says which of the two
 * offers it is making.
 */

import {
  forGrams,
  parseDecimal,
  sum,
  type CookedBatch,
  type FoodItem,
  type Helped,
  type Macros,
  type PotInput,
  type Recipe,
} from "@calorio/core";
import { useMemo, useState } from "react";

/** One thing in the pan, at the weight it went in at. */
export interface PotItem {
  food: FoodItem;
  grams: number;
}

export interface PotDraft {
  items: PotItem[];
  /** What the pot is called. "New pot" until somebody says otherwise. */
  name: string;
  /** The saved meal it started from, if it started from one. */
  source: Recipe | null;
  /**
   * Whether a way in has been chosen at all.
   *
   * Distinct from "the pot is empty", and the distinction is a whole screen.
   * Nothing chosen yet is the entry state — a list of saved meals to start
   * from, with building it by hand offered underneath. An empty pot somebody
   * has already started is a *pot*, with an empty state that says so and a
   * chip row of what they have cooked with before. Collapsing the two sends
   * anyone who picks "start from scratch" straight back to the screen they just
   * left.
   */
  started: boolean;
  /** ×1, ×2, ×3 — the recipe, multiplied, for this pot only. */
  scale: number;
  /** Whether anything has been changed since it was loaded from a recipe. */
  edited: boolean;
  /** Whether the offer to save has been turned down for this pot. */
  saveDismissed: boolean;
  portions: number;
  /** The cooked weight as typed, empty until the pot is put on the scales. */
  cooked: string;
  bestBefore: string | null;
}

const EMPTY: PotDraft = {
  items: [],
  started: false,
  name: "New pot",
  source: null,
  scale: 1,
  edited: false,
  saveDismissed: false,
  portions: 4,
  cooked: "",
  bestBefore: null,
};

/**
 * What the pot comes to, at the scale it is being cooked at.
 *
 * Every figure the two screens print, derived in one place so they cannot
 * disagree. Removing an ingredient moves all of it at once, which is the whole
 * claim the builder makes about itself.
 */
export interface PotFigures {
  /** The scaled lines, which is what the builder lists and what gets sent. */
  lines: { food: FoodItem; grams: number; kcal: number }[];
  rawG: number;
  totals: Macros;
  empty: boolean;
  /** The weight typed on the scales, or null while the pot is unweighed. */
  cookedG: number | null;
  /** What one portion weighs — measured if weighed, a fraction of the raw sum
   *  if not. Null for an empty pot, which has no portions to speak of. */
  portionG: number | null;
  perPortion: Macros;
  /** Cooked density, and only ever that. Null while the pot is unweighed. */
  kcalPer100Cooked: number | null;
  /** Water off, in grams and as a share of the raw weight. */
  lossG: number | null;
  lossPct: number | null;
}

const per100 = (food: FoodItem): Macros => ({
  kcal: food.kcal_per_100,
  protein_g: food.protein_g_per_100,
  carbs_g: food.carbs_g_per_100,
  fat_g: food.fat_g_per_100,
});

export function potFigures(draft: PotDraft): PotFigures {
  const lines = draft.items.map((item) => {
    const grams = item.grams * draft.scale;
    return { food: item.food, grams, kcal: forGrams(per100(item.food), grams).kcal };
  });
  const rawG = lines.reduce((total, line) => total + line.grams, 0);
  const totals = sum(lines.map((line) => forGrams(per100(line.food), line.grams)));
  const empty = lines.length === 0 || rawG <= 0;

  const typed = parseDecimal(draft.cooked);
  const cookedG = draft.cooked !== "" && Number.isFinite(typed) && typed > 0 ? typed : null;
  const portions = Math.max(1, draft.portions);

  // A weighed pot divides its measured weight; an unweighed one divides the raw
  // sum, which is the same arithmetic the server has always done and is not a
  // gram figure anybody should log against — see `weightNote` in the divider.
  const basis = cookedG ?? rawG;
  const portionG = empty ? null : basis / portions;

  const factor = empty ? 0 : 1 / portions;
  const perPortion: Macros = {
    kcal: totals.kcal * factor,
    protein_g: totals.protein_g * factor,
    carbs_g: totals.carbs_g * factor,
    fat_g: totals.fat_g * factor,
  };

  return {
    lines,
    rawG,
    totals,
    empty,
    cookedG,
    portionG,
    perPortion,
    kcalPer100Cooked: empty || cookedG === null ? null : (totals.kcal / cookedG) * 100,
    lossG: empty || cookedG === null ? null : rawG - cookedG,
    lossPct: empty || cookedG === null ? null : ((rawG - cookedG) / rawG) * 100,
  };
}

/**
 * Whether this pot is worth offering to save, and which offer to make.
 *
 * Two offers, because they are two different sentences. A pot built here is a
 * new meal; a pot loaded from a recipe and then changed is a *fork* of one, and
 * saying "save this as a meal" about it would hide the fact that the user is
 * about to end up with two chillis. A pot cooked exactly as its recipe is never
 * asked, because the recipe already exists.
 */
export function saveOffer(
  draft: PotDraft,
  figures: PotFigures,
): { suggestion: string; action: string } | null {
  if (figures.empty || draft.saveDismissed) return null;
  const changed = draft.edited || draft.scale !== 1;
  if (draft.source && !changed) return null;

  return draft.source
    ? {
        suggestion: `This no longer matches your saved ${draft.source.name}. Keep the changes as their own meal?`,
        action: "Save as new",
      }
    : {
        suggestion: "Worth cooking again? Save it as a meal and the next pot starts here.",
        action: "Name and save",
      };
}

/**
 * The one write a pot makes, as the server takes it.
 *
 * A pot loaded from a saved meal and cooked as written names the meal; one
 * built here, or changed, sends what went in and the server writes the
 * composition down so the pot has something to be made of. Pure, so the page
 * that stores a pot and the test that checks the body agree by construction.
 */
export function potInput(draft: PotDraft, figures: PotFigures): PotInput {
  return {
    ...(draft.source && !draft.edited && draft.scale === 1
      ? { recipe_id: draft.source.id }
      : {
          name: draft.name,
          items: figures.lines.map((line) => ({
            food_item_id: line.food.id,
            grams: line.grams,
          })),
        }),
    portions_made: Math.max(1, draft.portions),
    ...(figures.cookedG !== null ? { cooked_weight_g: figures.cookedG } : {}),
    ...(draft.bestBefore ? { best_before: draft.bestBefore } : {}),
  };
}

/**
 * The diary row a helping out of a pot shows before the write lands.
 *
 * Built here because two surfaces eat from a pot — the add sheet's Stored rail
 * and the pot's own page — and the row they show has to be the same row. The
 * figures come from `helpingFor`, which already scaled the pot's per-portion
 * numbers; this only says which columns they go in.
 *
 * `foodItemId` is null: a pot's own food is unlisted and the tile does not
 * carry its id, so a re-weigh before the queue drains scales off the snapshot.
 */
export function portionHint(
  batch: Pick<CookedBatch, "recipe_name">,
  helped: Helped,
  localDate: string,
): {
  name: string;
  quantityG: number;
  kcal: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  foodItemId: null;
  localDate: string;
} {
  return {
    name: batch.recipe_name,
    // A weighed pot has a gram figure; an unweighed one is a fraction, and the
    // column is NOT NULL, so nought says "not weighed" the same way a quick add
    // does.
    quantityG: helped.grams,
    kcal: helped.kcal,
    protein_g: helped.protein_g,
    carbs_g: helped.carbs_g,
    fat_g: helped.fat_g,
    foodItemId: null,
    localDate,
  };
}

/**
 * Whether a pot has to be eaten today.
 *
 * On the day it has to go, and only then. Anything looser and the amber is on
 * half the rail, which is the same as being on none of it. One predicate for
 * the tile and the page, so the two cannot disagree about which pot is urgent.
 */
export function urgent(batch: Pick<CookedBatch, "best_before">, todayIso: string): boolean {
  return batch.best_before !== null && batch.best_before <= todayIso;
}

export interface PotActions {
  /** Start from a saved meal, at the weights it was saved with. */
  fromRecipe: (recipe: Recipe, items: PotItem[]) => void;
  /** Start from nothing — add what went in as it goes in. */
  fromScratch: () => void;
  add: (food: FoodItem, grams: number) => void;
  reweigh: (index: number, grams: number) => void;
  remove: (index: number) => void;
  rename: (name: string) => void;
  setScale: (scale: number) => void;
  setPortions: (portions: number) => void;
  setCooked: (cooked: string) => void;
  setBestBefore: (date: string | null) => void;
  dismissSave: () => void;
  reset: () => void;
}

export function usePotDraft(): { draft: PotDraft; figures: PotFigures; actions: PotActions } {
  const [draft, setDraft] = useState<PotDraft>(EMPTY);
  const figures = useMemo(() => potFigures(draft), [draft]);

  const actions = useMemo<PotActions>(
    () => ({
      fromRecipe: (recipe, items) =>
        setDraft((current) => ({
          ...EMPTY,
          portions: current.portions,
          started: true,
          items,
          name: recipe.name,
          source: recipe,
          // `servings_made` is what the recipe says it makes, and it is the
          // right opening bid for how this pot gets cut. The user can still
          // move it; what they must not have to do is discover it was ignored.
          ...(recipe.servings_made > 0 ? { portions: Math.round(recipe.servings_made) } : {}),
        })),
      fromScratch: () =>
        setDraft((current) => ({ ...EMPTY, portions: current.portions, started: true })),
      add: (food, grams) =>
        setDraft((current) => ({
          ...current,
          // Stored unscaled, so ×2 keeps meaning "twice the recipe" rather than
          // doubling whatever was improvised on top of it as well.
          items: [...current.items, { food, grams: grams / current.scale }],
          edited: true,
          saveDismissed: false,
        })),
      reweigh: (index, grams) =>
        setDraft((current) => ({
          ...current,
          items: current.items.map((item, i) =>
            i === index ? { ...item, grams: grams / current.scale } : item,
          ),
          edited: true,
          saveDismissed: false,
        })),
      remove: (index) =>
        setDraft((current) => ({
          ...current,
          items: current.items.filter((_, i) => i !== index),
          edited: true,
          saveDismissed: false,
        })),
      rename: (name) => setDraft((current) => ({ ...current, name })),
      setScale: (scale) => setDraft((current) => ({ ...current, scale, saveDismissed: false })),
      setPortions: (portions) =>
        setDraft((current) => ({ ...current, portions: Math.min(12, Math.max(1, portions)) })),
      setCooked: (cooked) => setDraft((current) => ({ ...current, cooked })),
      setBestBefore: (bestBefore) => setDraft((current) => ({ ...current, bestBefore })),
      dismissSave: () => setDraft((current) => ({ ...current, saveDismissed: true })),
      reset: () => setDraft(EMPTY),
    }),
    [],
  );

  return { draft, figures, actions };
}
